import { Link, useParams } from 'react-router-dom'

import { PageState } from '../app/PublicLayout.jsx'
import { assetUrl, useRecord } from '../app/useSiteData.js'
import { BlockRenderer } from '../blocks/index.js'
import { useI18n } from '../lib/i18n.jsx'
import {
  Chip,
  Container,
  DefinitionList,
  EmptyState,
  ExternalLink,
  Media,
  Prose,
  Row,
  Section,
} from '../ui/index.js'
import { DEMO_KIND_KEY } from './DemoList.jsx'

/**
 * One demo.
 *
 * The mirror of the project page: a project opens with the facts of its grant,
 * a demo opens with the thing itself — the cover, then the write-up. The facts
 * a demo does have (category, year, related project, links) are few enough to
 * sit under the body rather than gate it.
 *
 * 单个演示样例。
 *
 * 与项目页互为镜像：项目页以立项事实开场，演示样例页以东西本身开场——先是封面，
 * 再是正文。演示样例确实有的那几项事实（类别、时间、相关项目、链接）数量很少，
 * 放在正文之下即可，不必挡在前面。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function DemoDetail() {
  const { id } = useParams()
  const { t, pick } = useI18n()
  const { data: record, loading, error, reload } = useRecord('demos', id)

  const missing = error && error.status === 404
  const title = record ? pick(record.title) : ''
  const summary = record ? pick(record.summary) : ''
  const cover = record ? assetUrl(record.cover) : ''
  const keywords = record ? pick(record.keywords) : []

  const facts = record
    ? [
        {
          label: t('demos.kind'),
          value: DEMO_KIND_KEY[record.kind] ? t(DEMO_KIND_KEY[record.kind]) : '',
        },
        { label: t('demos.year'), value: record.year },
        { label: t('demos.relatedProject'), value: pick(record.relatedProject) },
        {
          label: t('publications.code'),
          value: record.codeUrl ? (
            <ExternalLink href={record.codeUrl}>{record.codeUrl}</ExternalLink>
          ) : (
            ''
          ),
        },
        {
          label: t('demos.video'),
          value: record.videoUrl ? (
            <ExternalLink href={record.videoUrl}>{record.videoUrl}</ExternalLink>
          ) : (
            ''
          ),
        },
      ]
    : []

  return (
    <Section className="demo-detail">
      <Container width="narrow">
        <nav className="detail-breadcrumb" aria-label={t('demos.title')}>
          <Link className="detail-breadcrumb__link" to="/research">
            {t('nav.research')}
          </Link>
          <span className="detail-breadcrumb__sep" aria-hidden="true">
            /
          </span>
          <Link className="detail-breadcrumb__link" to="/research/demos">
            {t('demos.title')}
          </Link>
        </nav>

        <PageState
          loading={loading}
          error={missing ? null : error}
          onRetry={reload}
          isEmpty={missing || !record}
          empty={
            <EmptyState
              title={t('demos.notFound')}
              body={t('error.notFoundBody')}
              action={
                <Link className="link-button link-button--ghost" to="/research/demos">
                  {t('demos.backToList')}
                </Link>
              }
            />
          }
        >
          {record && (
            <article className="demo-article">
              <header className="demo-article__head">
                <h1 className="demo-article__title">{title}</h1>
                {summary && <p className="demo-article__lede">{summary}</p>}
                {Array.isArray(keywords) && keywords.length > 0 && (
                  <Row className="demo-article__keywords" gap="tight">
                    {keywords.map((keyword) => (
                      <Chip key={keyword}>{keyword}</Chip>
                    ))}
                  </Row>
                )}
              </header>

              {cover && (
                <Media ratio="16 / 9" className="demo-article__cover">
                  <img src={cover} alt={title} loading="lazy" decoding="async" />
                </Media>
              )}

              <Prose className="demo-article__body">
                <BlockRenderer blocks={record.blocks} />
              </Prose>

              <DefinitionList className="demo-article__facts" items={facts} />
            </article>
          )}
        </PageState>
      </Container>
    </Section>
  )
}
